import { create } from 'zustand'
import { invoicesAPI, paymentMethodsAPI, settingsAPI, isOnline } from '../services/api';
import { fetchAll, create as createItem, update as updateItem, deleteItem } from '../services/apiHelpers';

/**
 * Invoice Store
 * Manages invoice creation state and calculations
 *
 * Protocols Applied:
 * - P-API-001: RESTful API Integration
 * - P-OFFLINE-001: Offline-First Strategy
 * - P-STATE-001: Zustand State Management
 */
const useInvoiceStore = create((set, get) => ({
  // ==================== State ====================
  invoices: [], // All invoices from API
  selectedCustomer: null,
  selectedInvoice: null,
  lineItems: [],
  notes: '',
  subtotal: 0,
  tax: 0,
  taxRate: 0.10, // 10% Kuwait VAT
  total: 0,
  isLoading: false,
  isLoadingDetails: false,
  error: null,

  // Payment state
  payments: [], // All payments for all invoices
  isLoadingPayments: false,
  paymentError: null,

  // Payment methods state
  paymentMethods: [], // All available payment methods from API
  isLoadingPaymentMethods: false,
  paymentMethodsError: null,

  // ==================== Actions ====================

  /**
   * Fetch all invoices from API
   * @returns {Promise<Array>} Invoices array
   */
  fetchInvoices: async () => {
    try {
      set({ loading: true, error: null });

      const response = await invoicesAPI.getAll();
      const data = response.data.data || response.data;

      // Map snake_case fields to camelCase for consistency
      const mappedInvoices = data.map(invoice => ({
        id: invoice.id,
        invoiceNumber: invoice.invoice_number || invoice.invoiceNumber,
        status: invoice.status,
        customer: {
          id: invoice.customer?.id || invoice.customer_id,
          name: invoice.customer?.name || '',
          phone: invoice.customer?.phone || '',
          address: invoice.customer?.address || ''
        },
        subtotal: parseFloat(invoice.subtotal || 0),
        tax: parseFloat(invoice.tax_amount || invoice.tax || 0),
        total: parseFloat(invoice.total || 0),
        paidAmount: parseFloat(invoice.paid_amount || 0),
        balance: parseFloat(invoice.balance || 0),
        notes: invoice.notes || '',
        date: invoice.invoice_date || invoice.date,
        createdAt: invoice.created_at || invoice.createdAt,
        updatedAt: invoice.updated_at || invoice.updatedAt
      }));

      set({ invoices: mappedInvoices, loading: false });
      return mappedInvoices;
    } catch (error) {
      set({ loading: false, error: error.message });
      console.warn('Failed to fetch invoices from API, using local cache:', error.message);
      return get().invoices;
    }
  },

  /**
   * Set selected customer
   * @param {Object} customer - Customer object
   */
  setCustomer: (customer) => {
    set({ selectedCustomer: customer })
  },

  /**
   * Add line item (product) from vehicle inventory
   * @param {Object} product - Product object from vehicle inventory (has quantity, unit_price, etc.)
   */
  addLineItem: (product) => {
    const { lineItems } = get()

    // Get available stock from vehicle inventory
    const availableStock = product.quantity || 0
    const productId = product.product_id || product.id
    const productName = product.product_name || product.name
    const price = product.unit_price || product.price || 0

    // Check if product already exists in invoice
    const existingIndex = lineItems.findIndex(item => item.productId === productId)

    if (existingIndex >= 0) {
      // Increase quantity if exists
      const updated = [...lineItems]
      const newQuantity = updated[existingIndex].quantity + 1

      // Check if new quantity exceeds available stock
      if (newQuantity > availableStock) {
        set({
          error: `لا يمكن إضافة أكثر من ${availableStock} ${product.unit || 'وحدة'} - هذا كل ما هو متوفر في السيارة`
        })
        return
      }

      updated[existingIndex].quantity = newQuantity
      updated[existingIndex].total = newQuantity * updated[existingIndex].price
      set({ lineItems: updated, error: null })
    } else {
      // Check if product has stock
      if (availableStock <= 0) {
        set({ error: 'هذا المنتج غير متوفر في السيارة حالياً' })
        return
      }

      // Add new line item
      const newItem = {
        id: Date.now(), // Temporary ID
        productId: productId,
        productName: productName,
        price: price,
        quantity: 1,
        availableStock: availableStock, // Store for validation
        availableQuantity: availableStock, // For display in LineItemRow
        unit: product.unit || 'وحدة',
        category: product.category || product.category_name || 'غير مصنف',
        total: price
      }
      set({ lineItems: [...lineItems, newItem], error: null })
    }

    // Recalculate totals
    get().calculateTotals()
  },

  /**
   * Update line item
   * @param {number} itemId - Line item ID
   * @param {Object} updates - Updated fields
   */
  updateLineItem: (itemId, updates) => {
    const { lineItems } = get()

    // If updating quantity, validate against available stock
    if (updates.quantity !== undefined) {
      const item = lineItems.find(i => i.id === itemId)
      if (item && item.availableStock && updates.quantity > item.availableStock) {
        set({
          error: `الكمية المتاحة في السيارة: ${item.availableStock} ${item.unit || 'وحدة'} فقط`
        })
        return
      }
    }

    const updated = lineItems.map(item => {
      if (item.id === itemId) {
        const newItem = { ...item, ...updates }
        // Recalculate item total
        newItem.total = newItem.quantity * newItem.price
        return newItem
      }
      return item
    })
    set({ lineItems: updated, error: null })

    // Recalculate totals
    get().calculateTotals()
  },

  /**
   * Remove line item
   * @param {number} itemId - Line item ID
   */
  removeLineItem: (itemId) => {
    const { lineItems } = get()
    const filtered = lineItems.filter(item => item.id !== itemId)
    set({ lineItems: filtered })

    // Recalculate totals
    get().calculateTotals()
  },

  /**
   * Calculate subtotal, tax, and total
   */
  calculateTotals: () => {
    const { lineItems, taxRate } = get()

    // Calculate subtotal
    const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0)

    // Calculate tax
    const tax = subtotal * taxRate

    // Calculate total
    const total = subtotal + tax

    set({ subtotal, tax, total })
  },

  /**
   * Set notes
   * @param {string} notes - Invoice notes
   */
  setNotes: (notes) => {
    set({ notes })
  },

  /**
   * Reset invoice state
   */
  resetInvoice: () => {
    set({
      selectedCustomer: null,
      lineItems: [],
      notes: '',
      subtotal: 0,
      tax: 0,
      total: 0,
      error: null
    })
  },

  /**
   * Save invoice (API call)
   * @param {boolean} isDraft - Save as draft or submit
   * @param {object} paymentData - Payment method and amount { method: 'cash'|'credit', paidAmount: number }
   * @param {object} discountData - Discount information { type: 'fixed'|'percentage', value: number }
   */
  saveInvoice: async (isDraft = false, paymentData = {}, discountData = {}) => {
    const { selectedCustomer, lineItems, notes, subtotal, taxRate } = get()

    // Validation
    if (!selectedCustomer) {
      set({ error: 'يجب اختيار عميل' })
      return { success: false, error: 'يجب اختيار عميل' }
    }

    if (lineItems.length === 0) {
      set({ error: 'يجب إضافة منتج واحد على الأقل' })
      return { success: false, error: 'يجب إضافة منتج واحد على الأقل' }
    }

    set({ isLoading: true, error: null })

    try {
      // Generate invoice number (INV-YYYYMMDD-XXXXX)
      const today = new Date()
      const dateStr = today.toISOString().split('T')[0].replace(/-/g, '')
      const randomNum = Math.floor(Math.random() * 99999).toString().padStart(5, '0')
      const invoiceNumber = `INV-${dateStr}-${randomNum}`

      // Calculate discount amount
      const discountAmount = discountData.value
        ? (discountData.type === 'percentage'
          ? (subtotal * discountData.value / 100)
          : discountData.value)
        : 0

      // Calculate totals AFTER discount
      const totalAfterDiscount = subtotal - discountAmount
      const taxAmount = totalAfterDiscount * taxRate
      const finalTotal = totalAfterDiscount + taxAmount

      // Prepare invoice data
      const invoiceData = {
        customer_id: selectedCustomer.id,
        invoice_number: invoiceNumber,
        invoice_date: today.toISOString().split('T')[0],
        status: paymentData.method || 'cash',
        paid_amount: paymentData.paidAmount ?? (paymentData.method === 'cash' ? finalTotal : 0),
        payment_method_id: paymentData.payment_method_id || null, // Payment method ID
        subtotal: subtotal,
        discount_amount: discountAmount,
        discount_type: discountData.type || 'fixed',
        discount_value: discountData.value || 0,
        tax_amount: taxAmount,
        total: finalTotal,
        notes: notes || '',
        items: lineItems.map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
          unit_price: item.price, // Fixed: Use unit_price instead of price
          total: item.total
        }))
      }

      console.log('💾 Saving invoice:', invoiceData)
      console.log('📦 Line items details:', lineItems.map(item => ({
        productId: item.productId,
        productName: item.productName,
        price: item.price,
        quantity: item.quantity,
        total: item.total
      })))

      // Call actual API
      const response = await invoicesAPI.create(invoiceData)
      const savedInvoice = response.data.data || response.data

      set({ isLoading: false })

      // Reset after successful save
      get().resetInvoice()

      return {
        success: true,
        invoice: savedInvoice,
        message: isDraft ? 'تم حفظ المسودة بنجاح' : 'تم إنشاء الفاتورة بنجاح'
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'فشل في حفظ الفاتورة'
      set({ isLoading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Clear error
   */
  clearError: () => {
    set({ error: null })
  },

  /**
   * Get invoice by ID (for details page)
   * NOW CONNECTED TO BACKEND API ✅
   * @param {string|number} invoiceId - Invoice ID
   */
  getInvoiceById: async (invoiceId) => {
    set({ isLoadingDetails: true, error: null, selectedInvoice: null })

    try {
      // API call to backend
      const response = await invoicesAPI.getById(invoiceId)
      const invoiceData = response.data.data || response.data

      // Debug: Log raw API response
      console.log('📦 [InvoiceStore] Raw API Response:', invoiceData)
      console.log('🔑 [InvoiceStore] Permissions:', {
        can_edit: invoiceData.can_edit,
        can_delete: invoiceData.can_delete,
        created_at: invoiceData.created_at
      })

      // Map API response to frontend format
      const invoice = {
        id: invoiceData.id,
        invoiceNumber: invoiceData.invoice_number || invoiceData.invoiceNumber,
        status: invoiceData.status,
        customer: invoiceData.customer || {
          id: invoiceData.customer_id,
          name: '',
          phone: '',
          address: ''
        },
        lineItems: (invoiceData.items || invoiceData.lineItems || []).map(item => ({
          id: item.id,
          productId: item.product_id || item.productId,
          productName: item.product?.name || item.productName || '',
          category: item.product?.category || '',
          availableQuantity: parseFloat(item.product?.representative_stock || item.product?.stock || 0),
          unit: item.product?.unit || 'وحدة',
          quantity: parseFloat(item.quantity || 0),
          price: parseFloat(item.unit_price || item.price || 0), // Backend uses unit_price
          total: parseFloat(item.total || 0),
          product: item.product || null // Include full product details
        })),
        sales_representative: invoiceData.sales_representative || null,
        payment_method: invoiceData.payment_method || null,
        due_date: invoiceData.due_date || null,
        is_overdue: invoiceData.is_overdue || false,
        subtotal: parseFloat(invoiceData.subtotal || 0),
        discount_type: invoiceData.discount_type || 'fixed',
        discount_value: parseFloat(invoiceData.discount_value || 0),
        discount_amount: parseFloat(invoiceData.discount_amount || 0),
        tax: parseFloat(invoiceData.tax_amount || invoiceData.tax || 0), // Backend uses tax_amount
        taxRate: parseFloat(invoiceData.tax_rate || invoiceData.taxRate || 0.10), // Default 10% Kuwait VAT
        total: parseFloat(invoiceData.total || 0),
        paidAmount: parseFloat(invoiceData.paid_amount || 0), // Initial paid amount from invoice creation
        balance: parseFloat(invoiceData.balance || 0), // Remaining balance
        notes: invoiceData.notes || '',
        date: invoiceData.invoice_date || invoiceData.date,
        createdAt: invoiceData.created_at || invoiceData.createdAt,
        updatedAt: invoiceData.updated_at || invoiceData.updatedAt,
        // Permissions - from backend
        can_edit: invoiceData.can_edit || false,
        can_delete: invoiceData.can_delete || false
      }

      console.log('✅ [InvoiceStore] Mapped Invoice:', {
        id: invoice.id,
        can_edit: invoice.can_edit,
        can_delete: invoice.can_delete
      })

      set({
        selectedInvoice: invoice,
        isLoadingDetails: false
      })

      return invoice
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'فشل في تحميل الفاتورة'
      set({
        isLoadingDetails: false,
        error: errorMessage
      })
      return null
    }
  },

  /**
   * Update invoice status
   * NOW CONNECTED TO BACKEND API ✅
   * @param {string|number} invoiceId - Invoice ID
   * @param {string} newStatus - New status (cash, credit, paid)
   */
  updateInvoiceStatus: async (invoiceId, newStatus) => {
    set({ isLoading: true, error: null })

    try {
      // API call to update invoice status
      const response = await invoicesAPI.update(invoiceId, { status: newStatus })

      const { selectedInvoice, invoices } = get()

      // Update selectedInvoice if it's the one being modified
      if (selectedInvoice && selectedInvoice.id == invoiceId) {
        set({
          selectedInvoice: {
            ...selectedInvoice,
            status: newStatus,
            updatedAt: new Date().toISOString()
          }
        })
      }

      // Update in invoices list too
      const updatedInvoices = invoices.map(inv =>
        inv.id == invoiceId ? { ...inv, status: newStatus } : inv
      )
      set({ invoices: updatedInvoices, isLoading: false })

      return {
        success: true,
        message: 'تم تحديث حالة الفاتورة بنجاح'
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'فشل في تحديث حالة الفاتورة'
      set({
        isLoading: false,
        error: errorMessage
      })
      return {
        success: false,
        error: errorMessage
      }
    }
  },

  /**
   * Load invoice data for editing
   * @param {Object} invoice - Invoice object to load
   */
  loadInvoiceForEdit: (invoice) => {
    if (!invoice) return

    // Map line items and recalculate available quantity for edit mode
    // In edit mode, available = current stock + quantity in this invoice
    const mappedLineItems = (invoice.lineItems || []).map(item => ({
      ...item,
      // Add back the quantity from this invoice to available stock
      // because it was already deducted when invoice was created
      availableQuantity: parseFloat(item.availableQuantity || 0) + parseFloat(item.quantity || 0)
    }))

    set({
      selectedInvoice: invoice,
      selectedCustomer: invoice.customer,
      lineItems: mappedLineItems,
      notes: invoice.notes || '',
      subtotal: invoice.subtotal || 0,
      tax: invoice.tax || 0,
      total: invoice.total || 0
    })
  },

  /**
   * Update existing invoice
   * @param {string} invoiceId - Invoice ID
   * @param {Object} data - Updated invoice data
   * @returns {Promise<Object>} Update result
   */
  updateInvoice: async (invoiceId, data) => {
    set({ isLoading: true, error: null })

    try {
      // Calculate discount amount
      const discountAmount = data.discountAmount
        ? (data.discountType === 'percentage'
          ? (data.subtotal * data.discountAmount / 100)
          : data.discountAmount)
        : 0

      // Format data for backend API (same format as saveInvoice)
      const formattedData = {
        customer_id: data.selectedCustomer?.id,
        notes: data.notes || '',
        subtotal: data.subtotal,
        discount_amount: discountAmount,
        discount_type: data.discountType || 'fixed',
        discount_value: data.discountAmount || 0,
        tax_amount: data.tax,
        total: data.total,
        status: data.paymentMethod || 'cash',
        payment_method_id: data.paymentMethod === 'cash' && data.selectedPaymentMethod ? data.selectedPaymentMethod.id : null,
        items: (data.lineItems || []).map(item => ({
          product_id: item.productId,
          quantity: item.quantity,
          unit_price: item.price,
          total: item.total
        }))
      }

      console.log('📝 [InvoiceStore] Updating invoice:', invoiceId, formattedData)

      // Call API
      const response = await invoicesAPI.update(invoiceId, formattedData)
      const updatedInvoice = response.data.data || response.data

      // Update in store
      const { invoices } = get()
      const updatedInvoices = invoices.map(inv =>
        inv.id == invoiceId ? { ...inv, ...updatedInvoice } : inv
      )

      set({ invoices: updatedInvoices, isLoading: false })

      return {
        success: true,
        invoice: updatedInvoice,
        message: 'تم تعديل الفاتورة بنجاح'
      }

    } catch (error) {
      const errorMessage = error.response?.data?.message || 'فشل في تعديل الفاتورة'
      set({ isLoading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Delete invoice
   * @param {string} invoiceId - Invoice ID to delete
   * @returns {Promise<Object>} Delete result
   */
  deleteInvoice: async (invoiceId) => {
    return await deleteItem(invoicesAPI.delete, invoiceId, set, get, 'invoices');
  },

  // ==================== Payment Actions ====================

  /**
   * Add new payment to an invoice
   * ✅ CONNECTED TO BACKEND API
   * @param {string|number} invoiceId - Invoice ID
   * @param {Object} paymentData - Payment details
   * @returns {Promise<Object>} Add payment result
   */
  addPayment: async (invoiceId, paymentData) => {
    set({ isLoadingPayments: true, paymentError: null })

    try {
      // Validation
      if (!paymentData.amount || paymentData.amount <= 0) {
        throw new Error('يجب إدخال مبلغ صحيح')
      }

      if (!paymentData.paymentMethod) {
        throw new Error('يجب اختيار طريقة الدفع')
      }

      // Call actual API endpoint
      const response = await invoicesAPI.addPayment(invoiceId, paymentData)
      const receiptData = response.data.data || response.data

      // Create payment object from receipt
      const newPayment = {
        id: receiptData.id,
        invoiceId: invoiceId,
        amount: parseFloat(receiptData.amount),
        paymentMethod: paymentData.paymentMethod, // Use the one we sent, not from receipt
        paymentDate: receiptData.payment_date || receiptData.paymentDate || paymentData.paymentDate,
        notes: receiptData.notes || paymentData.notes || '',
        createdAt: receiptData.created_at || receiptData.createdAt,
        receiptNumber: receiptData.receipt_number || receiptData.receiptNumber
      }

      // Add to payments array
      const { payments } = get()
      set({
        payments: [...payments, newPayment],
        isLoadingPayments: false
      })

      // Update invoice payment status if it's the selected invoice
      const { selectedInvoice } = get()
      if (selectedInvoice && selectedInvoice.id == invoiceId) {
        const totalPaid = get().getTotalPaid(invoiceId)
        const paymentStatus = get().getPaymentStatus(invoiceId)

        set({
          selectedInvoice: {
            ...selectedInvoice,
            totalPaid,
            paymentStatus,
            updatedAt: new Date().toISOString()
          }
        })
      }

      return {
        success: true,
        message: 'تم إضافة الدفعة بنجاح',
        payment: newPayment
      }
    } catch (error) {
      const errorMessage = error.message || error.response?.data?.message || 'فشل في إضافة الدفعة'
      set({
        isLoadingPayments: false,
        paymentError: errorMessage
      })
      return {
        success: false,
        error: errorMessage
      }
    }
  },

  /**
   * Fetch payments from API for an invoice
   * @param {string|number} invoiceId - Invoice ID
   * @returns {Promise<void>}
   */
  fetchPayments: async (invoiceId) => {
    set({ isLoadingPayments: true, paymentError: null })

    try {
      const response = await invoicesAPI.getPayments(invoiceId)
      const receiptsData = response.data.data || response.data

      // Map payment_method_id to payment method code
      const mapPaymentMethodId = (receipt) => {
        // If paymentMethod object exists (from API with relationships), use its code
        if (receipt.paymentMethod && receipt.paymentMethod.code) {
          const codeMap = {
            'CASH': 'cash',
            'CARD': 'credit_card',
            'TRANSFER': 'bank_transfer',
            'CHEQUE': 'cheque',
            'EWALLET': 'e_wallet'
          }
          return codeMap[receipt.paymentMethod.code] || 'cash'
        }

        // Try to find from payment methods store by ID
        const { paymentMethods } = get()
        if (receipt.payment_method_id && paymentMethods.length > 0) {
          const method = paymentMethods.find(m => m.id === receipt.payment_method_id)
          if (method) {
            return method.value
          }
        }

        // Fallback to default
        return 'cash'
      }

      // Map receipts to payments
      const fetchedPayments = receiptsData.map(receipt => ({
        id: receipt.id,
        invoiceId: invoiceId,
        amount: parseFloat(receipt.amount),
        paymentMethod: mapPaymentMethodId(receipt),
        paymentDate: receipt.receipt_date || receipt.payment_date || receipt.paymentDate,
        notes: receipt.notes || '',
        createdAt: receipt.created_at || receipt.createdAt,
        receiptNumber: receipt.receipt_number || receipt.receiptNumber
      }))

      // Remove old payments for this invoice and add new ones
      const { payments } = get()
      const otherPayments = payments.filter(p => p.invoiceId != invoiceId)

      set({
        payments: [...otherPayments, ...fetchedPayments],
        isLoadingPayments: false
      })

    } catch (error) {
      console.error('Error fetching payments:', error)
      set({
        isLoadingPayments: false,
        paymentError: error.message || 'فشل في جلب الدفعات'
      })
    }
  },

  /**
   * Get all payments for an invoice
   * @param {string|number} invoiceId - Invoice ID
   * @returns {Array} Array of payments
   */
  getPaymentsByInvoice: (invoiceId) => {
    const { payments } = get()
    return payments.filter(payment => payment.invoiceId == invoiceId)
  },

  /**
   * Calculate total paid amount for an invoice
   * @param {string|number} invoiceId - Invoice ID
   * @returns {number} Total paid amount
   */
  getTotalPaid: (invoiceId) => {
    const { selectedInvoice } = get()

    // Start with initial paid amount from invoice (if this is the selected invoice)
    let initialPaid = 0
    if (selectedInvoice && selectedInvoice.id == invoiceId && selectedInvoice.paidAmount) {
      initialPaid = selectedInvoice.paidAmount
    }

    // Add all additional payments (receipts)
    const invoicePayments = get().getPaymentsByInvoice(invoiceId)
    const paymentsSum = invoicePayments.reduce((sum, payment) => sum + payment.amount, 0)

    const total = initialPaid + paymentsSum
    // Round to 3 decimals to avoid floating point precision issues
    return parseFloat(total.toFixed(3))
  },

  /**
   * Calculate remaining balance for an invoice
   * @param {string|number} invoiceId - Invoice ID
   * @returns {number} Remaining balance
   */
  getRemainingBalance: (invoiceId) => {
    const { selectedInvoice } = get()
    if (!selectedInvoice || selectedInvoice.id != invoiceId) { // Use == for loose comparison
      return 0
    }

    const totalPaid = get().getTotalPaid(invoiceId)
    const remaining = selectedInvoice.total - totalPaid
    // Round to 3 decimals to avoid floating point precision issues
    return parseFloat(remaining.toFixed(3))
  },

  /**
   * Get payment status for an invoice
   * @param {string|number} invoiceId - Invoice ID
   * @returns {string} Payment status: 'paid' | 'partial' | 'unpaid'
   */
  getPaymentStatus: (invoiceId) => {
    const { selectedInvoice } = get()
    if (!selectedInvoice || selectedInvoice.id != invoiceId) { // Use == for loose comparison
      return 'unpaid'
    }

    const totalPaid = get().getTotalPaid(invoiceId)
    const total = selectedInvoice.total

    if (totalPaid === 0) {
      return 'unpaid'
    } else if (totalPaid >= total) {
      return 'paid'
    } else {
      return 'partial'
    }
  },

  /**
   * Clear payment error
   */
  clearPaymentError: () => {
    set({ paymentError: null })
  },

  /**
   * Fetch all available payment methods from API
   * @returns {Promise<Array>} Payment methods array
   */
  fetchPaymentMethods: async () => {
    set({ isLoadingPaymentMethods: true, paymentMethodsError: null })

    try {
      const response = await paymentMethodsAPI.getAll()

      if (response.data.success) {
        const methods = response.data.data || []

        // Map to frontend format with codes
        const mappedMethods = methods.map(method => {
          const codeToValue = {
            'CASH': 'cash',
            'CARD': 'credit_card',
            'TRANSFER': 'bank_transfer',
            'CHEQUE': 'cheque',
            'EWALLET': 'e_wallet'
          }

          return {
            id: method.id,
            code: method.code,
            value: codeToValue[method.code] || method.code.toLowerCase(),
            name: method.name,
            name_e: method.name_e,
            requires_reference: method.requires_reference
          }
        })

        set({
          paymentMethods: mappedMethods,
          isLoadingPaymentMethods: false
        })

        return mappedMethods
      } else {
        throw new Error(response.data.message || 'Failed to fetch payment methods')
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error)
      set({
        paymentMethodsError: error.message,
        isLoadingPaymentMethods: false
      })
      return []
    }
  },

  /**
   * Get payment method by value/code
   * @param {string} value - Payment method value (e.g., 'cash', 'credit_card')
   * @returns {Object|null} Payment method object
   */
  getPaymentMethodByValue: (value) => {
    const { paymentMethods } = get()
    return paymentMethods.find(method => method.value === value) || null
  },

  /**
   * Fetch tax rate from settings
   * @returns {Promise<number>} Tax rate
   */
  fetchTaxRate: async () => {
    try {
      const taxRate = await settingsAPI.getTaxRate()
      set({ taxRate: parseFloat(taxRate) / 100 }) // Convert from percentage to decimal
      return taxRate
    } catch (error) {
      console.warn('Failed to fetch tax rate, using default 14%:', error.message)
      set({ taxRate: 0.14 })
      return 0.14
    }
  }
}))

export default useInvoiceStore
